import { useContext, useMemo, type ContextType } from 'react';
import { Theme, useMediaQuery } from '@mui/material';

import { DrawerContext } from '../contexts';

type DrawerContextValue = ContextType<typeof DrawerContext>;

type UseDrawerReturn = DrawerContextValue & {
  /** Drawer behaves persistently (docked) from md and up */
  isDrawerPersistent: boolean;
  /** Convenience flag for large screens */
  isDesktop: boolean;
  /** Suggested MUI Drawer variant based on screen and user preference */
  variant: 'temporary' | 'persistent' | 'permanent';
  /** Left offset in px to align AppBar/content when drawer is open */
  leftOffset: number;
};

export const useDrawer = (): UseDrawerReturn => {
  const ctx = useContext(DrawerContext);

  // Screen breakpoints
  const isMobile = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('md'),
  );
  const isPersistent = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.up('md'),
  );
  const isDesktop = useMediaQuery<Theme>((theme) => theme.breakpoints.up('lg'));

  // Choose variant: mobile = temporary; desktop keep-open = permanent; otherwise persistent
  const variant: 'temporary' | 'persistent' | 'permanent' = isMobile
    ? 'temporary'
    : ctx.isDrawerKeepOpen
      ? 'permanent'
      : 'persistent';

  // Compute left offset for layout syncing
  const leftOffset = !isMobile && ctx.isDrawerOpen ? ctx.drawerWidth : 0;

  return useMemo(
    () => ({
      ...ctx,
      // Provide accurate, derived flags and helpers
      isDrawerPersistent: isPersistent,
      isDesktop,
      variant,
      leftOffset,
    }),
    [ctx, isPersistent, isDesktop, variant, leftOffset],
  );
};
